import React from 'react'
import { css } from '@emotion/css'
import { range } from 'lodash'
import { Text, Link } from '@contrast-security-inc/design-system-typography'
import { theme } from '@contrast-security-inc/design-system-foundations'
import {
  facetNameToApplicationFilterNameMap,
  convertFacetsToFilters
} from '../../header/filters'
import { GradeBadge, GradeThermometer } from '../../../grades'
import {
  LoadingMaskGradeBadgeSmall,
  LoadingMaskRectangle,
  LoadingMaskThermometer
} from '../../../../legacy-design-system/loading-masks'
import { GradeValue } from '../../../../graphql/schema/types/grade-value'
import { SelectedValueReportingFacet } from '../../../../graphql/schema/types/value-reporting'
import { useApplicationSecuritySummaryFacets } from '../../api/use-application-security-summary-facets'

interface GradeRecord {
  readonly value: GradeValue
  readonly count: number
}

interface Props {
  readonly organizationUuid: string
  readonly selectedFacets: SelectedValueReportingFacet[]
  readonly data: GradeRecord[]
}

const cssThermometerLayout = css`
  display: flex;
  flex-direction: column;
`
const cssThermometerRow = (count) => css`
  display: flex;
  margin: 4px 0;
  cursor: ${count > 0 ? 'pointer' : 'default'};
`
const cssThermometerRatio = css`
  flex: 1 0 89.5%;
  display: flex;
  align-items: center;
  padding: 0 10px 0 10px;
`
const cssThermometerCount = css`
  color: ${theme.color.neutral60};
  display: flex;
  align-items: center;
`

export const GradeBadgeThermometerSummary = ({
  organizationUuid,
  selectedFacets,
  data
}: Props) => {
  const testId = 'value-reporting-overall-apps-breakdown'
  const max = data.reduce((prev, curr) => prev + curr.count, 0)

  const {
    data: { applicationSecuritySummaryFacetsRequest }
  } = useApplicationSecuritySummaryFacets({
    organizationUuid,
    selectedFacets
  })

  const additionalFilters = convertFacetsToFilters(
    facetNameToApplicationFilterNameMap,
    selectedFacets,
    applicationSecuritySummaryFacetsRequest
  )

  return (
    <div
      className={cssThermometerLayout}
      data-testid={testId}
      data-e2e={testId}
    >
      {data.map(({ count, value }, index) => {
        const href = `#/${organizationUuid}/applications?quickFilter=LICENSED&scores=${value}${additionalFilters}`
        const showLinks = count > 0
        const WrapperComponent = showLinks ? Link : React.Fragment
        const wrapperProps = showLinks
          ? { style: { textDecoration: 'none', display: 'inline' }, href }
          : {}
        const gradeTestId = `value-reporting-overall-apps-breakdown-${index}-grade-${value}`
        const ratioTestId = `value-reporting-overall-apps-breakdown-${index}-thermometer`
        const countTestId = `value-reporting-overall-apps-breakdown-${index}-count-${count}`

        return (
          <WrapperComponent {...wrapperProps} key={index}>
            <div className={cssThermometerRow(count)}>
              <GradeBadge
                value={value}
                large={false}
                dataTestId={gradeTestId}
                e2eTestId={gradeTestId}
              />
              <div className={cssThermometerRatio}>
                <GradeThermometer
                  grade={value}
                  ratio={max === 0 ? 0 : count / max}
                  dataTestId={ratioTestId}
                  e2eTestId={ratioTestId}
                />
              </div>
              <div className={cssThermometerCount}>
                <Text
                  font="labelSm"
                  data-testid={countTestId}
                  data-e2e={countTestId}
                >
                  ({count})
                </Text>
              </div>
            </div>
          </WrapperComponent>
        )
      })}
    </div>
  )
}

const cssMain = css`
  display: flex;
  align-items: stretch;
  > * {
    display: flex;
    flex-direction: column;
    justify-content: space-around;
  }
`
const cssBadges = css`
  flex-grow: 0;
  margin-right: 10px;
  > * {
    margin: 4px 0;
  }
`
const cssThermometers = css`
  flex-grow: 1;
  margin-right: 10px;
`

export const GradeBadgeThermometerSummaryLoadingView = () => {
  const getRange = () => range(0, 5)

  return (
    <div className={cssMain}>
      <div className={cssBadges}>
        {getRange().map((value) => (
          <LoadingMaskGradeBadgeSmall
            key={value}
            className={css`
              margin: 4px 0;
            `}
          />
        ))}
      </div>
      <div className={cssThermometers}>
        {getRange().map((value) => (
          <LoadingMaskThermometer key={value} />
        ))}
      </div>
      <div>
        {getRange().map((value) => (
          <LoadingMaskRectangle key={value} width={26} height={17} />
        ))}
      </div>
    </div>
  )
}
